import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { getAuthUser } from "../../../helpers/utils"; 
import "../../../App.css"; 


const ColumnSelectionModal = ({
  isOpen,
  toggle,
  columns, 
  selectedColumns, 
  setSelectedColumns,
  handleSave,
}) => {
  const { user } = getAuthUser();
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const userId = user?.ID;
  const isRTL = i18n.dir() === "rtl";  

  // Effect to set initial selections based on UserId, if columns have that info
  useEffect(() => {
    // Ensure userId is treated as a number for the .includes() check.
    if (columns && userId !== undefined && userId !== null) {
      const numericIdToCompare = Number(userId);

      const userSpecificDefaultColumns = columns
        .filter(
          (col) =>
            col.UserID &&
            Array.isArray(col.UserID) && 
            col.UserID.includes(numericIdToCompare)
        )
        .map((col) => col.ID);

      if (userSpecificDefaultColumns.length > 0) {
        setSelectedColumns(userSpecificDefaultColumns);
      }
      // If no user-specific columns are found, selectedColumns remains as is.
    }
  }, [columns, userId, setSelectedColumns]);

  const handleCheckboxChange = (columnId) => {
    setSelectedColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((col) => col !== columnId)
        : [...prev, columnId]
    );
  };

  const allDisplayedColumnIds = useMemo(() => columns ? columns.map(col => col.ID) : [], [columns]);

  const allSelected = useMemo(() => {
    if (!columns || columns.length === 0) return false;
    // Ensure selectedColumns is an array before using .includes
    const currentSelected = Array.isArray(selectedColumns) ? selectedColumns : [];
    return allDisplayedColumnIds.every(id => currentSelected.includes(id)) && currentSelected.length === allDisplayedColumnIds.length;
  }, [selectedColumns, columns, allDisplayedColumnIds]);

  const handleSelectAllChange = (isChecked) => {
    if (isChecked) {
      setSelectedColumns(allDisplayedColumnIds);
    } else {
      setSelectedColumns([]);
    }
  };

  const selectedCount = Array.isArray(selectedColumns) ? selectedColumns.length : 0;
  const totalCount = columns?.length || 0;
  const canSave = selectedCount > 0; // Condition for enabling the save button

  return (
    <Modal isOpen={isOpen} toggle={toggle} className={isRTL ? "rtl-modal" : "ltr-modal"} centered>
      <ModalHeader tag="div" toggle={toggle} className="align-items-center">
        <h5 className="modal-title fw-semibold mb-0">{t("SelectColumns")}</h5>
      </ModalHeader>
      <ModalBody className="modal-body-scrollable" style={{ direction:'ltr'}}>
        {columns && columns.length > 0 && (
          <FormGroup
            className={`mb-3 p-2 rounded shadow-sm bg-light d-flex justify-content-between align-items-center ${
              isRTL ? "flex-row-reverse" : ""
            }`}
          >
            <Label htmlFor="selectAllSwitch" className="fw-medium mb-0" style={{ cursor: "pointer" }}>
              {t("Select All")}
            </Label>
            <Input
              type="switch"
              id="selectAllSwitch"
              checked={allSelected}
              onChange={(e) => handleSelectAllChange(e.target.checked)}
              style={{ cursor: "pointer" }}
              aria-label={t("Select All")}
            />
          </FormGroup>
        )}

        {columns?.map((column) => (
          <div
            key={column.ID}
            className={`column-item mb-2 p-2 rounded column-item-hover d-flex justify-content-between align-items-center ${
              isRTL ? "flex-row-reverse" : ""
            }`}
          >
            <Label
              htmlFor={`column-switch-${column.ID}`}
              className="mb-0"
              style={{ cursor: "pointer" }}
            >
              {language === "ar" ? column.ValueAr : column.ValueEn}
            </Label>
            <Input
              type="switch"
              id={`column-switch-${column.ID}`}
              checked={Array.isArray(selectedColumns) && selectedColumns.includes(column.ID)}
              onChange={() => handleCheckboxChange(column.ID)}
              style={{ cursor: "pointer" }}
              aria-label={language === "ar" ? column.ValueAr : column.ValueEn}
            />
          </div>
        ))}
        
        {columns && columns.length > 0 && (
          <div className={`mt-3 text-muted small ${isRTL ? "text-end" : "text-start"}`}>
            {t("selectedCount", { count: selectedCount, total: totalCount, defaultValue: `${selectedCount} ${t("of")} ${totalCount} ${t("selected")}` })}
          </div>
        )}
        {!canSave && columns && columns.length > 0 && (
          <div className={`mt-2 text-danger small ${isRTL ? "text-end" : "text-start"}`}>
            {t("Please select at least one column.")}
          </div>
        )}
         {(!columns || columns.length === 0) && (
          <p className={`text-muted ${isRTL ? "text-end" : "text-start"}`}>{t("NoColumnsAvailable", "No columns available to select.")}</p>
        )}
      </ModalBody>
      <ModalFooter className={isRTL ? "flex-row-reverse" : ""} style={{direction: 'ltr'}}>
        <Button color="secondary" outline onClick={toggle} className={isRTL ? "ms-2" : "me-2"}>
          {t("Cancel")}
        </Button>
        <Button color="primary" onClick={handleSave} disabled={!canSave}>
          {t("Apply")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ColumnSelectionModal;